import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { Moon, Sun } from "lucide-react";
import type { GlobePoint, GlobePlaylist } from "#shared/schemas/globe.ts";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { QueryErrorState } from "@web/features/diagnostics/QueryErrorState.tsx";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { useGlobeData } from "@web/features/globe/api.ts";
import { seedAmbianceClock } from "@web/features/globe/ambiance.ts";
import { BatchQueuePanel } from "@web/features/globe/BatchQueuePanel.tsx";
import { buildDevArtistPoints } from "@web/features/globe/devPoints.ts";
import {
  GlobeCanvas,
  type GlobeCanvasHandle,
} from "@web/features/globe/GlobeCanvas.tsx";
import { LayerStrip } from "@web/features/globe/LayerStrip.tsx";
import { PlaylistPicker } from "@web/features/globe/PlaylistPicker.tsx";
import { ShotPanel } from "@web/features/globe/ShotPanel.tsx";
import { useShotSession } from "@web/features/globe/useShotSession.ts";
import { useGlobeStore } from "@web/stores/globe.ts";
import { useGlobeShotStore } from "@web/stores/globeShot.ts";
import { useDetailStore } from "@web/stores/detail.ts";
import { useUiStore } from "@web/stores/ui.ts";
import { cn } from "@web/lib/utils.ts";

/**
 * Full-pane 3D globe — visual layers, cinematic focus, shot mode.
 */
export function GlobePage() {
  const globe = useGlobeData();
  const [params] = useSearchParams();
  const canvasRef = useRef<GlobeCanvasHandle>(null);
  const [ready, setReady] = useState(false);
  const [glyph, setGlyph] = useState(() => seedAmbianceClock().glyph);

  const selectedPlaylistIds = useGlobeStore((s) => s.selectedPlaylistIds);
  const selectPlaylist = useGlobeStore((s) => s.selectPlaylist);
  const deselectPlaylist = useGlobeStore((s) => s.deselectPlaylist);
  const setCountryPanel = useGlobeStore((s) => s.setCountryPanel);
  const countryPanel = useGlobeStore((s) => s.countryPanel);
  const layers = useGlobeStore((s) => s.layers);
  const focusedPointId = useGlobeStore((s) => s.focusedPointId);
  const setFocusedPointId = useGlobeStore((s) => s.setFocusedPointId);
  const pendingFocus = useGlobeStore((s) => s.pendingFocus);
  const consumePendingFocus = useGlobeStore((s) => s.consumePendingFocus);
  const requestFocus = useGlobeStore((s) => s.requestFocus);
  const setFocusSuspendAutoRotate = useGlobeStore((s) => s.setFocusSuspendAutoRotate);
  const setSheetOpen = useUiStore((s) => s.setSheetOpen);
  const setTarget = useDetailStore((s) => s.setTarget);
  const openDetail = useOpenDetail();

  const recording = useGlobeShotStore((s) => s.recording);
  const pendingRecord = useGlobeShotStore((s) => s.pendingRecord);
  const consumePendingRecord = useGlobeShotStore((s) => s.consumePendingRecord);
  const batchStatus = useGlobeShotStore((s) => s.batchStatus);

  const cleanStage = layers.cleanStage || recording;
  const chromeHidden = cleanStage;
  const shotBusy = recording || batchStatus === "running" || batchStatus === "paused";

  const displayPoints = useMemo(() => {
    if (!globe.data) return [];
    if (params.get("dev") !== "points") return globe.data.points;
    const n = params.get("n") === "500" ? 500 : 200;
    return buildDevArtistPoints(globe.data.points, n);
  }, [params, globe.data]);

  const playlistsById = useMemo(() => {
    const m = new Map<string, GlobePlaylist>();
    for (const pl of globe.data?.playlists ?? []) m.set(pl.id, pl);
    return m;
  }, [globe.data]);

  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (deepLinkDone.current || !globe.data) return;
    const raw = params.get("focus");
    if (!raw) return;
    deepLinkDone.current = true;
    if (raw.startsWith("country:")) {
      requestFocus({ kind: "country", code: raw.slice("country:".length).toUpperCase() });
      return;
    }
    requestFocus({ kind: "artist", id: raw });
  }, [params, globe.data, requestFocus]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setGlyph(seedAmbianceClock().glyph);
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const resolveFocusPoint = useCallback(
    (target: { kind: "country"; code: string } | { kind: "artist"; id: string }) => {
      if (!globe.data) return null;
      if (target.kind === "country") {
        return (
          globe.data.points.find((p) => p.countryCode === target.code) ?? null
        );
      }
      return (
        globe.data.points.find((p) => p.artistIds.includes(target.id)) ?? null
      );
    },
    [globe.data],
  );

  const {
    previewCurrent,
    recordCurrent,
    runBatch,
    pauseBatch,
    stopBatch,
  } = useShotSession(canvasRef, resolveFocusPoint);

  /** Shot target: focused pin, or the country currently open in the sheet. */
  const shotTarget = useMemo(() => {
    if (!globe.data) return null;
    const id = focusedPointId ?? countryPanel?.pointId ?? null;
    if (!id) return null;
    return globe.data.points.find((p) => p.id === id) ?? null;
  }, [focusedPointId, countryPanel?.pointId, globe.data]);

  // Run pending browse focus after textures load.
  useEffect(() => {
    if (!ready || !pendingFocus) return;
    const target = consumePendingFocus();
    if (!target) return;
    const point = resolveFocusPoint(target);
    if (!point) return;
    setFocusSuspendAutoRotate(true);
    void canvasRef.current?.focusOnPoint(point);
  }, [
    ready,
    pendingFocus,
    consumePendingFocus,
    resolveFocusPoint,
    setFocusSuspendAutoRotate,
  ]);

  // Pending single record from sheet / country panel.
  // No directory picker here — user gesture is gone after navigation.
  useEffect(() => {
    if (!ready || !pendingRecord) return;
    const target = consumePendingRecord();
    if (!target) return;
    const point = resolveFocusPoint(target);
    if (!point) return;
    const artistName = target.kind === "country" ? target.artistName : undefined;
    void recordCurrent(point, artistName, { allowPicker: false });
  }, [ready, pendingRecord, consumePendingRecord, resolveFocusPoint, recordCurrent]);

  const onCountryClick = useCallback(
    (point: GlobePoint, playlistId: string | null) => {
      let artistIds = point.artistIds;
      let playlistName: string | null = null;
      if (playlistId) {
        const pl = playlistsById.get(playlistId);
        if (pl) {
          playlistName = pl.name;
          const set = new Set(pl.artistIds);
          artistIds = point.artistIds.filter((id) => set.has(id));
        }
      }
      setTarget(null);
      setFocusedPointId(point.id);
      setCountryPanel({
        pointId: point.id,
        countryCode: point.countryCode ?? "",
        label: point.label,
        artistIds,
        playlistId,
        playlistName,
      });
      setSheetOpen(true);
    },
    [playlistsById, setCountryPanel, setFocusedPointId, setSheetOpen, setTarget],
  );

  const onPlaylistObjectClick = useCallback(
    (playlistId: string) => {
      const pl = playlistsById.get(playlistId);
      if (!pl) return;
      if (selectedPlaylistIds.includes(playlistId)) {
        deselectPlaylist(playlistId);
        return;
      }
      selectPlaylist(playlistId);
      setCountryPanel(null);
      openDetail({ type: "playlist", id: pl.id, label: pl.name }, { mode: "reset" });
    },
    [
      playlistsById,
      selectedPlaylistIds,
      selectPlaylist,
      deselectPlaylist,
      setCountryPanel,
      openDetail,
    ],
  );

  if (globe.isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-bg">
        <Skeleton className="size-64 rounded-full opacity-40" />
      </div>
    );
  }

  if (globe.isError) {
    return (
      <QueryErrorState
        error={globe.error}
        onRetry={() => void globe.refetch()}
        actionLabel="load globe"
      />
    );
  }

  if (!globe.data) {
    return (
      <EmptyState
        title="Couldn't load the globe"
        description="Check that enrichment has artist countries, then retry."
      />
    );
  }

  if (globe.data.points.length === 0) {
    return (
      <EmptyState
        title="No countries yet"
        description="Artists need a MusicBrainz country from enrichment before they appear here."
      />
    );
  }

  return (
    <div className="relative h-full min-h-0 overflow-hidden bg-bg">
      <GlobeCanvas
        ref={canvasRef}
        points={displayPoints}
        playlists={globe.data.playlists}
        selectedPlaylistIds={selectedPlaylistIds}
        layers={layers}
        focusedPointId={focusedPointId}
        cleanStage={cleanStage}
        tooltipsOff={cleanStage}
        onCountryClick={onCountryClick}
        onPlaylistObjectClick={onPlaylistObjectClick}
        onUserInteract={() => setFocusSuspendAutoRotate(false)}
        onReady={() => setReady(true)}
        onFocusComplete={(id) => {
          setFocusedPointId(id);
          setFocusSuspendAutoRotate(true);
        }}
      />

      {/* Left chrome: layers then shot — one column so they never fight the playlist. */}
      {!chromeHidden && (
        <div className="pointer-events-none absolute top-3 left-3 z-20 flex flex-col items-start gap-2">
          <LayerStrip />
          <ShotPanel
            disabled={shotBusy}
            onPreview={() => void previewCurrent(shotTarget)}
            onRecord={() =>
              void recordCurrent(
                shotTarget,
                countryPanel?.label && shotTarget?.id === countryPanel.pointId
                  ? countryPanel.label
                  : undefined,
              )
            }
          />
        </div>
      )}

      <BatchQueuePanel
        hidden={chromeHidden && !shotBusy}
        disabled={shotBusy && batchStatus === "idle"}
        points={globe.data.points}
        playlists={globe.data.playlists}
        onRun={() => void runBatch()}
        onPause={() => pauseBatch()}
        onStop={() => stopBatch()}
      />

      {!chromeHidden && (
        <PlaylistPicker
          playlists={globe.data.playlists}
          selectedIds={selectedPlaylistIds}
          onSelect={selectPlaylist}
          onDeselect={deselectPlaylist}
        />
      )}

      {!chromeHidden && (
        <div
          className={cn(
            "pointer-events-none absolute right-3 bottom-3 z-10 flex size-8 items-center justify-center rounded-full",
            "border border-line/60 bg-panel/50 text-muted backdrop-blur-sm",
          )}
          aria-hidden
        >
          {glyph === "sun" ? (
            <Sun className="size-3.5 opacity-80" />
          ) : (
            <Moon className="size-3.5 opacity-80" />
          )}
        </div>
      )}

      {params.get("dev") === "points" && !chromeHidden && (
        <div className="pointer-events-none absolute right-3 bottom-14 z-10 rounded-md border border-line bg-panel/80 px-2 py-1 text-xs text-faint backdrop-blur-sm">
          dev points · {displayPoints.length}
        </div>
      )}
    </div>
  );
}
